import { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useUserModules } from '../../api';
import {
  useSmsWebhookList,
  useVoiceWebhookList,
} from '@/features/webhooks/api';
import {
  templateFeatures,
  type VoiceCampaignFormValues,
} from '../../types';

interface Props {
  values: VoiceCampaignFormValues;
  setValues: (patch: Partial<VoiceCampaignFormValues>) => void;
}

interface SmsBlockProps {
  flag: boolean;
  setFlag: (v: boolean) => void;
  url: string;
  setUrl: (v: string) => void;
  label: string;
  description: string;
  smsWebhookFlag: boolean;
  smsWebhookOptions: Array<{ value: string; label: string }>;
}

function SmsBlock({
  flag,
  setFlag,
  url,
  setUrl,
  label,
  description,
  smsWebhookFlag,
  smsWebhookOptions,
}: SmsBlockProps) {
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-muted-foreground">{description}</div>
        </div>
        <Switch checked={flag} onCheckedChange={setFlag} />
      </div>
      {flag && (
        <div className="space-y-2">
          {smsWebhookFlag ? (
            <Select value={url} onValueChange={setUrl}>
              <SelectTrigger>
                <SelectValue placeholder="Pick an SMS webhook" />
              </SelectTrigger>
              <SelectContent>
                {smsWebhookOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://sms-gateway.example.com/send"
              className="font-mono text-xs"
            />
          )}
        </div>
      )}
    </div>
  );
}

export function StepNotifications({ values, setValues }: Props) {
  const modules = useUserModules();
  const voiceHooks = useVoiceWebhookList();
  const smsHooks = useSmsWebhookList();
  const features = templateFeatures(values.templateId);

  const webhookFlag = useMemo(
    () => (modules.data ?? []).some((m) => m.moduleName === 'Webhooks'),
    [modules.data],
  );
  const smsWebhookFlag = useMemo(
    () => (modules.data ?? []).some((m) => m.moduleName === 'SMS Webhooks'),
    [modules.data],
  );

  const smsOptions = (smsHooks.data ?? []).map((w) => ({
    value: String(w.id),
    label: w.webhookName,
  }));

  const showSuccess = features.smsSections.includes('success');
  const showFail = features.smsSections.includes('fail');
  const showDtmf = features.smsSections.includes('dtmf');
  const showPatchSuccess = features.smsSections.includes('patchSuccess');
  const showPatchFail = features.smsSections.includes('patchFail');
  const hasAnySms =
    showSuccess || showFail || showDtmf || showPatchSuccess || showPatchFail;

  if (!hasAnySms && !webhookFlag) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>
            This campaign type doesn&apos;t use per-call SMS or webhooks.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Move on to Review when you&apos;re ready.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {hasAnySms && (
        <Card>
          <CardHeader>
            <CardTitle>SMS on call outcome</CardTitle>
            <CardDescription>
              Trigger an SMS to the called party based on call result. Use an SMS webhook
              if configured; otherwise paste a gateway URL.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {showSuccess && (
              <>
                <SmsBlock
                  flag={values.sendSmsSuccess}
                  setFlag={(v) => setValues({ sendSmsSuccess: v })}
                  url={values.smsApiSuccess}
                  setUrl={(v) => setValues({ smsApiSuccess: v })}
                  label="SMS on success"
                  description="Sent when the call is answered and exceeds the success duration."
                  smsWebhookFlag={smsWebhookFlag}
                  smsWebhookOptions={smsOptions}
                />
                {values.sendSmsSuccess && (
                  <div className="space-y-2 rounded-md border border-border p-3">
                    <Label htmlFor="cd-sms">
                      Min call duration for success SMS (seconds)
                    </Label>
                    <Input
                      id="cd-sms"
                      inputMode="numeric"
                      value={String(values.callDurationSMS)}
                      onChange={(e) =>
                        setValues({
                          callDurationSMS:
                            Number(e.target.value.replace(/\D/g, '')) || 0,
                        })
                      }
                    />
                  </div>
                )}
              </>
            )}

            {showFail && (
              <SmsBlock
                flag={values.sendSmsFail}
                setFlag={(v) => setValues({ sendSmsFail: v })}
                url={values.smsApiFail}
                setUrl={(v) => setValues({ smsApiFail: v })}
                label="SMS on failure"
                description="Sent when the call doesn't connect or hangs up too early."
                smsWebhookFlag={smsWebhookFlag}
                smsWebhookOptions={smsOptions}
              />
            )}

            {showDtmf && (
              <SmsBlock
                flag={values.sendSmsDtmf}
                setFlag={(v) => setValues({ sendSmsDtmf: v })}
                url={values.smsApiDtmf}
                setUrl={(v) => setValues({ smsApiDtmf: v })}
                label="SMS on DTMF input"
                description="Sent when the user presses a valid DTMF digit."
                smsWebhookFlag={smsWebhookFlag}
                smsWebhookOptions={smsOptions}
              />
            )}

            {showPatchSuccess && (
              <SmsBlock
                flag={values.sendSmsPatchSuccess}
                setFlag={(v) => setValues({ sendSmsPatchSuccess: v })}
                url={values.callPatchSuccessMessage}
                setUrl={(v) => setValues({ callPatchSuccessMessage: v })}
                label="SMS on patch success"
                description="Sent when the call patches through to an agent."
                smsWebhookFlag={smsWebhookFlag}
                smsWebhookOptions={smsOptions}
              />
            )}

            {showPatchFail && (
              <SmsBlock
                flag={values.sendSmsPatchFail}
                setFlag={(v) => setValues({ sendSmsPatchFail: v })}
                url={values.callPatchFailMessage}
                setUrl={(v) => setValues({ callPatchFailMessage: v })}
                label="SMS on patch failure"
                description="Sent when no agent picks up."
                smsWebhookFlag={smsWebhookFlag}
                smsWebhookOptions={smsOptions}
              />
            )}
          </CardContent>
        </Card>
      )}

      {webhookFlag && (
        <Card>
          <CardHeader>
            <CardTitle>Outbound webhook</CardTitle>
            <CardDescription>
              Push call events to one of your configured voice webhooks.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-medium">Send webhook</div>
                <div className="text-xs text-muted-foreground">
                  Pick which webhook fires on each event.
                </div>
              </div>
              <Switch
                checked={values.sendWebhook}
                onCheckedChange={(v) => setValues({ sendWebhook: v })}
              />
            </div>
            {values.sendWebhook && (
              <div className="space-y-2">
                <Label htmlFor="webhook-id">Webhook</Label>
                <Select
                  value={values.webhookId}
                  onValueChange={(v) => setValues({ webhookId: v })}
                >
                  <SelectTrigger id="webhook-id">
                    <SelectValue
                      placeholder={
                        voiceHooks.isLoading ? 'Loading…' : 'Pick a webhook'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(voiceHooks.data ?? []).map((w) => (
                      <SelectItem key={w.id} value={String(w.id)}>
                        {w.webhookName} — {w.event}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
